import type { SupportedLanguage } from "../../../interfaces";
import {
  AnalyticsItemCategory,
  AnalyticsItemVariant,
} from "../analytics.types";
import { gaPushEvent } from "../base.analytics";
import {
  AnalyticsItemReturnData,
  gaEventSelectItem,
  gaEventViewItem,
  gaEventViewItemList,
} from "../common.analytics";
import type { CongressAttribution } from "../../../domains/congress/congressAttribution";

// TODO: Missing events: select item, view item, etc.

/** Minimal playlist shape needed by analytics functions. */
export type PlaylistForAnalytics = {
  id: string;
  title: string;
  language: SupportedLanguage;
  sponsored: boolean;
  company?: { name: string } | null;
  medical_specialties?: { uid: string }[] | null;
};

enum Event {
  PLAYLIST_SAVE = "playlist_save",
  PLAYLIST_UNSAVE = "playlist_unsave",
  PLAYLIST_SHARE = "playlist_share",
}

type PlaylistItemPayload = {
  playlist: PlaylistForAnalytics;
  listName: string;
};

type PlaylistItemListPayload = {
  playlists: PlaylistForAnalytics[];
  listName: string;
};

function convertPlaylistToItemData(
  payload: PlaylistItemPayload
): AnalyticsItemReturnData {
  const { playlist, listName } = payload;
  return {
    item_id: playlist.id,
    item_name: playlist.title,
    item_brand: playlist.company?.name ?? "",
    item_category:
      playlist.medical_specialties?.map((spe) => spe.uid).join(",") ?? null,
    item_category2: playlist.sponsored
      ? AnalyticsItemCategory.SPONSORED
      : AnalyticsItemCategory.STANDARD,
    item_variant: AnalyticsItemVariant.PLAYLIST,
    item_list_name: listName,
  };
}

/**
 * Playlist page viewed
 */
export function gaEventViewItemPlaylist(
  payload: PlaylistItemPayload,
  congress: CongressAttribution | null
) {
  gaEventViewItem(convertPlaylistToItemData(payload), congress);
}

/**
 * List of playlists displayed in a page
 */
export function gaEventViewItemListPlaylist(payload: PlaylistItemListPayload) {
  gaEventViewItemList(
    payload.playlists.map((playlist) => {
      return convertPlaylistToItemData({
        playlist,
        listName: payload.listName,
      });
    })
  );
}

export function gaEventPlaylistShare(payload: PlaylistItemPayload) {
  gaPushEvent(Event.PLAYLIST_SHARE, {
    ecommerce: {
      items: [convertPlaylistToItemData(payload)],
    },
  });
}

export function gaEventPlaylistSave(payload: PlaylistItemPayload) {
  gaPushEvent(Event.PLAYLIST_SAVE, {
    status: "saved",
    ecommerce: {
      items: [convertPlaylistToItemData(payload)],
    },
  });
}

export function gaEventPlaylistUnsave(payload: PlaylistItemPayload) {
  gaPushEvent(Event.PLAYLIST_UNSAVE, {
    status: "unsaved",
    ecommerce: {
      items: [convertPlaylistToItemData(payload)],
    },
  });
}

export function gaEventSelectItemPlaylist(payload: PlaylistItemPayload) {
  gaEventSelectItem(convertPlaylistToItemData(payload));
}
